import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import AdminPagination from '../../components/AdminPagination';
import './Users.css';

const PAGINATION_MOBILE_BREAKPOINT = 768;
const ITEMS_PER_PAGE_DESKTOP = 10;
const ITEMS_PER_PAGE_MOBILE = 5;

const Users = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { showError, showSuccess } = useNotification();
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletDescription, setWalletDescription] = useState('');
  const [walletDeductAmount, setWalletDeductAmount] = useState('');
  const [walletDeductDescription, setWalletDeductDescription] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    birthdate: '',
    phoneNumber: '',
    role: 'customer',
    disabled: false,
    ownedRestaurants: [],
    ownedStores: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${PAGINATION_MOBILE_BREAKPOINT}px)`);
    const handler = () => setItemsPerPage(mql.matches ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, restaurantsRes, storesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/restaurants`),
        axios.get(`${API_URL}/admin/stores`)
      ]);
      setUsers(usersRes.data);
      setRestaurants(restaurantsRes.data);
      setStores(storesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingUser({});
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      birthdate: '',
      phoneNumber: '',
      role: 'customer',
      disabled: false,
      ownedRestaurants: [],
      ownedStores: []
    });
  };

  const handleEdit = async (user) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      password: '',
      birthdate: user.birthdate ? new Date(user.birthdate).toISOString().split('T')[0] : '',
      phoneNumber: user.phoneNumber || '',
      role: user.role || 'customer',
      disabled: user.disabled || false,
      ownedRestaurants: user.ownedRestaurants?.map(r => r._id || r) || [],
      ownedStores: user.ownedStores?.map(s => s._id || s) || []
    });
    // Fetch wallet for this user
    try {
      const response = await axios.get(`${API_URL}/admin/users/${user._id}/wallet`);
      setWallet(response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
      setWallet(null);
    }
    setWalletAmount('');
    setWalletDescription('');
    setWalletDeductAmount('');
    setWalletDeductDescription('');
  };

  const handleSave = async () => {
    try {
      if (editingUser._id) {
        // Update existing user - only send password if it's being changed
        const updateData = { ...formData };
        // Remove password from update if it's empty (not being changed)
        if (!updateData.password || updateData.password.trim() === '') {
          delete updateData.password;
        }
        await axios.patch(`${API_URL}/admin/users/${editingUser._id}`, updateData);
        showSuccess(t('admin.userSaved'));
      } else {
        // Create new user
        if (!formData.password) {
          showError(t('auth.password') + ' ' + t('admin.loading'));
          return;
        }
        await axios.post(`${API_URL}/admin/users`, formData);
        showSuccess(t('admin.userSaved'));
      }
      await fetchData();
      setEditingUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      showError(error.response?.data?.message || error.message);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    const userId = userToDelete._id;
    setUserToDelete(null);
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      await fetchData();
      showSuccess(t('admin.userDeleted'));
    } catch (error) {
      console.error('Error deleting user:', error);
      showError(error.response?.data?.message || error.message);
    }
  };

  const handleDisable = async (userId, disabled) => {
    try {
      await axios.patch(`${API_URL}/admin/users/${userId}/disable`, { disabled });
      await fetchData();
    } catch (error) {
      console.error('Error toggling user status:', error);
      showError(t('admin.userSaved'));
    }
  };

  const handleConfirmEmail = async (userId) => {
    try {
      await axios.patch(`${API_URL}/admin/users/${userId}/verify-email`);
      showSuccess(t('admin.emailVerified') || 'Email confirmed. User can now log in.');
      await fetchData();
      if (editingUser?._id === userId) {
        setEditingUser(prev => (prev && prev._id === userId ? { ...prev, emailVerified: true } : prev));
      }
    } catch (error) {
      console.error('Error confirming email:', error);
      showError(error.response?.data?.message || error.message);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      main_admin: '#e74c3c',
      admin: '#9b59b6',
      invoicer: '#1abc9c',
      restaurant_owner: '#9b59b6',
      restaurant_user: '#8e44ad',
      store_owner: '#3498db',
      store_user: '#2980b9',
      delivery_driver: '#f39c12',
      customer: '#81D000'
    };
    return colors[role] || '#95a5a6';
  };

  const getRoleDisplayLabel = (role) => {
    const labels = {
      main_admin: t('admin.mainAdmin'),
      admin: t('admin.roleAdmin'),
      invoicer: t('admin.invoicer'),
      restaurant_owner: t('admin.restaurantOwner'),
      restaurant_user: t('admin.restaurantUser'),
      store_owner: t('admin.storeOwner'),
      store_user: t('admin.storeUser'),
      delivery_driver: t('admin.deliveryDriver'),
      customer: t('admin.customer')
    };
    return labels[role] || role;
  };

  const filteredUsers = useMemo(() => {
    let result = users;
    const q = (searchQuery || '').toLowerCase().trim();
    if (q) {
      result = result.filter(u => {
        const name = `${(u.firstName || '')} ${(u.lastName || '')}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        const phone = (u.phoneNumber || '').replace(/\s/g, '');
        const qNorm = q.replace(/\s/g, '');
        return name.includes(q) || email.includes(q) || (qNorm && phone.includes(qNorm));
      });
    }
    if (filterRole) {
      result = result.filter(u => u.role === filterRole);
    }
    if (filterStatus === 'enabled') {
      result = result.filter(u => !u.disabled);
    } else if (filterStatus === 'disabled') {
      result = result.filter(u => u.disabled);
    }
    return result;
  }, [users, searchQuery, filterRole, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredUsers.length, itemsPerPage]);

  if (loading) {
    return (
      <div className="users-container">
        <div className="loading-spinner">{t('admin.loading')}</div>
      </div>
    );
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>{t('admin.users')}</h2>
        <button onClick={handleAddNew} className="add-btn">
          {t('admin.addNew')}
        </button>
      </div>

      <div className="users-toolbar">
        <div className="users-search">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.searchUsers') || 'Search by name, email or phone...'}
            className="users-search-input"
            aria-label={t('admin.searchUsers') || 'Search users'}
          />
        </div>
        <div className="users-filters">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="users-filter-select"
            aria-label={t('admin.filterByRole') || 'Filter by role'}
          >
            <option value="">{t('admin.allRoles') || 'All roles'}</option>
            <option value="customer">{t('admin.customer')}</option>
            <option value="main_admin">{t('admin.mainAdmin')}</option>
            <option value="admin">{t('admin.roleAdmin')}</option>
            <option value="invoicer">{t('admin.invoicer')}</option>
            <option value="restaurant_owner">{t('admin.restaurantOwner')}</option>
            <option value="restaurant_user">{t('admin.restaurantUser')}</option>
            <option value="store_owner">{t('admin.storeOwner')}</option>
            <option value="store_user">{t('admin.storeUser')}</option>
            <option value="delivery_driver">{t('admin.deliveryDriver')}</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="users-filter-select"
            aria-label={t('admin.filterByStatus') || 'Filter by status'}
          >
            <option value="">{t('admin.allStatuses') || 'All statuses'}</option>
            <option value="enabled">{t('admin.enabled')}</option>
            <option value="disabled">{t('admin.disabled')}</option>
          </select>
        </div>
        <div className="users-results-count">
          {filteredUsers.length} {filteredUsers.length === 1 ? (t('admin.user') || 'user') : (t('admin.users') || 'users')}
          {filteredUsers.length !== users.length && ` (${t('admin.of') || 'of'} ${users.length})`}
        </div>
      </div>

      <div className="users-table-wrapper">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('admin.firstName')}</th>
              <th>{t('admin.email')}</th>
              <th>{t('admin.phoneNumber')}</th>
              <th>{t('admin.role')}</th>
              <th>{t('admin.status')}</th>
              <th>{t('admin.emailVerification') || 'Email'}</th>
              <th>{t('admin.view')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {users.length === 0 ? t('admin.noOrders') : (t('admin.noMatchingUsers') || 'No users match your search or filters.')}
                </td>
              </tr>
            ) : (
              paginatedUsers.map(user => (
                <tr key={user._id}>
                  <td className="user-name" data-label={t('admin.firstName')}>{user.firstName} {user.lastName}</td>
                  <td className="user-email" data-label={t('admin.email')}>{user.email}</td>
                  <td className="user-phone" data-label={t('admin.phoneNumber')}>{user.phoneNumber || 'N/A'}</td>
                  <td data-label={t('admin.role')}>
                    <span 
                      className="role-badge" 
                      style={{ backgroundColor: getRoleBadgeColor(user.role) }}
                    >
                      {getRoleDisplayLabel(user.role)}
                    </span>
                  </td>
                  <td data-label={t('admin.status')}>
                    <span className={`status-badge ${user.disabled ? 'disabled' : 'active'}`}>
                      {user.disabled ? t('admin.disabled') : t('admin.enabled')}
                    </span>
                  </td>
                  <td data-label={t('admin.emailVerification') || 'Email'}>
                    {user.emailVerified === false ? (
                      <div className="email-verify-cell">
                        <span className="status-badge unverified">{t('admin.unverified') || 'Unverified'}</span>
                        <button
                          type="button"
                          onClick={() => handleConfirmEmail(user._id)}
                          className="confirm-email-btn"
                          title={t('admin.confirmEmailTooltip') || 'Confirm email so user can log in without verification link'}
                        >
                          {t('admin.confirmEmail') || 'Confirm email'}
                        </button>
                      </div>
                    ) : (
                      <span className="status-badge verified">{t('admin.verified') || 'Verified'}</span>
                    )}
                  </td>
                  <td data-label={t('admin.view')}>
                    <div className="action-buttons">
                      <button onClick={() => handleEdit(user)} className="edit-btn">{t('admin.edit')}</button>
                      <button 
                        onClick={() => handleDisable(user._id, !user.disabled)}
                        className={user.disabled ? 'enable-btn' : 'disable-btn'}
                      >
                        {user.disabled ? t('admin.enabled') : t('admin.disabled')}
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(user)}
                        className="delete-btn"
                      >
                        {t('admin.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredUsers.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />

      {editingUser !== null && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingUser._id ? t('admin.edit') + ' ' + t('admin.users') : t('admin.addNew')}</h2>
              <button className="close-btn" onClick={() => setEditingUser(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Password {!editingUser._id && '*'}</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={editingUser._id ? "Leave blank to keep current" : "Required for new users"}
                      required={!editingUser._id}
                    />
                  </div>
                  <div className="form-group">
                    <label>Birthdate *</label>
                    <input
                      type="date"
                      value={formData.birthdate}
                      onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
                      required={!editingUser._id}
                      disabled={!!editingUser._id}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="text"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                      required={!editingUser._id}
                    />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      disabled={currentUser?.role !== 'main_admin'}
                      title={currentUser?.role !== 'main_admin' ? (t('admin.onlyMainAdminCanChangeRole') || 'Only Main Admin can change user roles') : ''}
                    >
                      <option value="customer">{t('admin.customer')}</option>
                      <option value="main_admin">{t('admin.mainAdmin')}</option>
                      <option value="admin">{t('admin.roleAdmin')}</option>
                      <option value="invoicer">{t('admin.invoicer')}</option>
                      <option value="restaurant_owner">{t('admin.restaurantOwner')}</option>
                      <option value="restaurant_user">{t('admin.restaurantUser')}</option>
                      <option value="store_owner">{t('admin.storeOwner')}</option>
                      <option value="store_user">{t('admin.storeUser')}</option>
                      <option value="delivery_driver">{t('admin.deliveryDriver')}</option>
                    </select>
                  </div>
                  {editingUser._id && (
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.disabled}
                          onChange={(e) => setFormData({ ...formData, disabled: e.target.checked })}
                        />
                        {t('admin.disabled')}
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {(formData.role === 'restaurant_owner' || formData.role === 'restaurant_user') && (
                <div className="form-section">
                  <h3>{formData.role === 'restaurant_user' ? 'Assigned Restaurants' : 'Owned Restaurants'}</h3>
                  <div className="checkbox-list">
                    {restaurants.length === 0 ? (
                      <p className="no-items">No restaurants available</p>
                    ) : (
                      restaurants.map(restaurant => (
                        <label key={restaurant._id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.ownedRestaurants.includes(restaurant._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  ownedRestaurants: [...formData.ownedRestaurants, restaurant._id]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  ownedRestaurants: formData.ownedRestaurants.filter(id => id !== restaurant._id)
                                });
                              }
                            }}
                          />
                          <span>{restaurant.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {(formData.role === 'store_owner' || formData.role === 'store_user') && (
                <div className="form-section">
                  <h3>{formData.role === 'store_user' ? 'Assigned Stores' : 'Owned Stores'}</h3>
                  <div className="checkbox-list">
                    {stores.length === 0 ? (
                      <p className="no-items">No stores available</p>
                    ) : (
                      stores.map(store => (
                        <label key={store._id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.ownedStores.includes(store._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  ownedStores: [...formData.ownedStores, store._id]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  ownedStores: formData.ownedStores.filter(id => id !== store._id)
                                });
                              }
                            }}
                          />
                          <span>{store.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {editingUser._id && (
                <div className="form-section">
                  <h3>Wallet Management</h3>
                  {wallet ? (
                    <div className="wallet-info">
                      <div className="wallet-balance-display">
                        <label>Current Balance:</label>
                        <span className="wallet-balance-amount">€{wallet.balance?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="wallet-add-funds">
                        <div className="form-group">
                          <label>Add Funds (€)</label>
                          <input
                            type="number"
                            value={walletAmount}
                            onChange={(e) => setWalletAmount(e.target.value)}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('admin.description') || 'Description'} ({t('admin.optional') || 'optional'})</label>
                          <input
                            type="text"
                            value={walletDescription}
                            onChange={(e) => setWalletDescription(e.target.value)}
                            placeholder={t('admin.addFundsDescriptionPlaceholder') || 'Admin credit, refund, etc.'}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!walletAmount || parseFloat(walletAmount) <= 0) {
                              showError('Please enter a valid amount');
                              return;
                            }
                            try {
                              await axios.post(`${API_URL}/admin/users/${editingUser._id}/wallet/add`, {
                                amount: parseFloat(walletAmount),
                                ...(walletDescription.trim() ? { description: walletDescription.trim() } : {})
                              });
                              showSuccess(t('admin.walletFundsAdded') || 'Funds added successfully');
                              setWalletAmount('');
                              setWalletDescription('');
                              const response = await axios.get(`${API_URL}/admin/users/${editingUser._id}/wallet`);
                              setWallet(response.data);
                            } catch (error) {
                              showError(error.response?.data?.message || 'Failed to add funds');
                            }
                          }}
                          className="btn-add-funds"
                        >
                          {t('admin.addFunds') || 'Add Funds'}
                        </button>
                      </div>
                      <div className="wallet-deduct-funds">
                        <div className="form-group">
                          <label>{t('admin.deductFunds') || 'Deduct Funds'} (€)</label>
                          <input
                            type="number"
                            value={walletDeductAmount}
                            onChange={(e) => setWalletDeductAmount(e.target.value)}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('admin.description') || 'Description'} ({t('admin.optional') || 'optional'})</label>
                          <input
                            type="text"
                            value={walletDeductDescription}
                            onChange={(e) => setWalletDeductDescription(e.target.value)}
                            placeholder={t('admin.deductReasonPlaceholder') || 'Admin deduction, correction, etc.'}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!walletDeductAmount || parseFloat(walletDeductAmount) <= 0) {
                              showError(t('admin.enterValidAmount') || 'Please enter a valid amount');
                              return;
                            }
                            const amount = parseFloat(walletDeductAmount);
                            if (wallet.balance < amount) {
                              showError(t('admin.insufficientWalletBalance') || `Insufficient balance. Current: €${(wallet.balance || 0).toFixed(2)}`);
                              return;
                            }
                            try {
                              await axios.post(`${API_URL}/admin/users/${editingUser._id}/wallet/deduct`, {
                                amount,
                                ...(walletDeductDescription.trim() ? { description: walletDeductDescription.trim() } : {})
                              });
                              showSuccess(t('admin.walletFundsDeducted') || 'Funds deducted successfully');
                              setWalletDeductAmount('');
                              setWalletDeductDescription('');
                              const response = await axios.get(`${API_URL}/admin/users/${editingUser._id}/wallet`);
                              setWallet(response.data);
                            } catch (error) {
                              showError(error.response?.data?.message || 'Failed to deduct funds');
                            }
                          }}
                          className="btn-deduct-funds"
                        >
                          {t('admin.deductFunds') || 'Deduct Funds'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="wallet-loading">Loading wallet...</div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={() => { setEditingUser(null); setWallet(null); }} className="cancel-btn">Cancel</button>
              <button onClick={handleSave} className="save-btn">
                {editingUser._id ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {userToDelete && (
        <div className="modal-overlay" onClick={() => setUserToDelete(null)}>
          <div className="modal-content delete-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin.deleteUser') || 'Delete user'}</h2>
              <button type="button" className="close-btn" onClick={() => setUserToDelete(null)} aria-label={t('admin.close') || 'Close'}>×</button>
            </div>
            <div className="modal-body">
              <p className="delete-user-confirm-text">
                {t('admin.deleteUserConfirm') || 'Are you sure you want to delete this user? This action cannot be undone.'}
              </p>
              <p className="delete-user-name">
                <strong>{[userToDelete.firstName, userToDelete.lastName].filter(Boolean).join(' ').trim() || t('admin.user')}</strong>
                {userToDelete.email && <span> ({userToDelete.email})</span>}
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => setUserToDelete(null)} className="cancel-btn">{t('admin.cancel') || 'Cancel'}</button>
              <button type="button" onClick={handleDeleteConfirm} className="delete-btn">{t('admin.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
